/**
 Reports Page
 * Vista para visualizar informes y estadísticas del negocio
 */

import { useState, useMemo } from "react";
import { Icon } from "@/components/ui";
import styles from "./ReportsPage.module.css";

type ReportPeriod = "week" | "month" | "quarter" | "year";
type ReportType = "technicians" | "costs" | "materials" | "kpis";

export const ReportsPage = () => {
  const [period, setPeriod] = useState<ReportPeriod>("month");
  const [reportType, setReportType] = useState<ReportType>("technicians");

  // Datos mock - Servicios por Técnico
  const technicianServices = {
    week: [
      {
        name: "Juan Pérez",
        services: 12,
        hours: 32,
        efficiency: 95.5,
        revenue: 2840.0,
      },
      {
        name: "María García",
        services: 10,
        hours: 28,
        efficiency: 92.3,
        revenue: 2350.0,
      },
      {
        name: "Carlos Rodríguez",
        services: 8,
        hours: 24,
        efficiency: 88.7,
        revenue: 1920.0,
      },
    ],
    month: [
      {
        name: "Juan Pérez",
        services: 48,
        hours: 128,
        efficiency: 94.2,
        revenue: 11360.0,
      },
      {
        name: "María García",
        services: 42,
        hours: 115,
        efficiency: 91.8,
        revenue: 9870.0,
      },
      {
        name: "Carlos Rodríguez",
        services: 35,
        hours: 98,
        efficiency: 89.5,
        revenue: 8225.0,
      },
    ],
    quarter: [
      {
        name: "Juan Pérez",
        services: 144,
        hours: 384,
        efficiency: 93.8,
        revenue: 34080.0,
      },
      {
        name: "María García",
        services: 128,
        hours: 350,
        efficiency: 92.1,
        revenue: 30080.0,
      },
      {
        name: "Carlos Rodríguez",
        services: 105,
        hours: 294,
        efficiency: 90.2,
        revenue: 24675.0,
      },
    ],
    year: [
      {
        name: "Juan Pérez",
        services: 576,
        hours: 1536,
        efficiency: 93.5,
        revenue: 136320.0,
      },
      {
        name: "María García",
        services: 512,
        hours: 1400,
        efficiency: 91.9,
        revenue: 120320.0,
      },
      {
        name: "Carlos Rodríguez",
        services: 420,
        hours: 1176,
        efficiency: 89.8,
        revenue: 98700.0,
      },
    ],
  };

  // Datos mock - Costes por Cliente/Plaga
  const costsData = {
    week: {
      byClient: [
        {
          name: "Hotel Central",
          services: 3,
          materials: 145.5,
          labor: 240.0,
          total: 385.5,
        },
        {
          name: "Restaurante El Buen Sabor",
          services: 2,
          materials: 98.75,
          labor: 160.0,
          total: 258.75,
        },
        {
          name: "Supermercado La Plaza",
          services: 2,
          materials: 125.0,
          labor: 160.0,
          total: 285.0,
        },
      ],
      byPest: [
        {
          pest: "Cucarachas",
          occurrences: 5,
          materials: 185.5,
          labor: 400.0,
          total: 585.5,
        },
        {
          pest: "Roedores",
          occurrences: 3,
          materials: 142.75,
          labor: 240.0,
          total: 382.75,
        },
        {
          pest: "Hormigas",
          occurrences: 2,
          materials: 41.0,
          labor: 120.0,
          total: 161.0,
        },
      ],
    },
    month: [
      {
        name: "Hotel Central",
        services: 12,
        materials: 582.0,
        labor: 960.0,
        total: 1542.0,
      },
      {
        name: "Restaurante El Buen Sabor",
        services: 8,
        materials: 395.0,
        labor: 640.0,
        total: 1035.0,
      },
      {
        name: "Supermercado La Plaza",
        services: 9,
        materials: 500.0,
        labor: 720.0,
        total: 1220.0,
      },
    ],
    quarter: {
      byClient: [
        {
          name: "Hotel Central",
          services: 36,
          materials: 1746.0,
          labor: 2880.0,
          total: 4626.0,
        },
        {
          name: "Restaurante El Buen Sabor",
          services: 24,
          materials: 1185.0,
          labor: 1920.0,
          total: 3105.0,
        },
        {
          name: "Supermercado La Plaza",
          services: 27,
          materials: 1500.0,
          labor: 2160.0,
          total: 3660.0,
        },
      ],
      byPest: [
        {
          pest: "Cucarachas",
          occurrences: 45,
          materials: 1669.5,
          labor: 3600.0,
          total: 5269.5,
        },
        {
          pest: "Roedores",
          occurrences: 32,
          materials: 1370.0,
          labor: 2560.0,
          total: 3930.0,
        },
        {
          pest: "Hormigas",
          occurrences: 18,
          materials: 369.0,
          labor: 1440.0,
          total: 1809.0,
        },
      ],
    },
    year: {
      byClient: [
        {
          name: "Hotel Central",
          services: 144,
          materials: 6984.0,
          labor: 11520.0,
          total: 18504.0,
        },
        {
          name: "Restaurante El Buen Sabor",
          services: 96,
          materials: 4740.0,
          labor: 7680.0,
          total: 12420.0,
        },
        {
          name: "Supermercado La Plaza",
          services: 108,
          materials: 6000.0,
          labor: 8640.0,
          total: 14640.0,
        },
      ],
      byPest: [
        {
          pest: "Cucarachas",
          occurrences: 180,
          materials: 6678.0,
          labor: 14400.0,
          total: 21078.0,
        },
        {
          pest: "Roedores",
          occurrences: 128,
          materials: 5480.0,
          labor: 10240.0,
          total: 15720.0,
        },
        {
          pest: "Hormigas",
          occurrences: 72,
          materials: 1476.0,
          labor: 5760.0,
          total: 7236.0,
        },
      ],
    },
  };

  // Datos mock - Consumo de Materiales
  const materialsConsumption = {
    week: [
      {
        material: "Insecticida Premium",
        quantity: 15,
        unit: "L",
        cost: 300.0,
        services: 8,
      },
      {
        material: "Raticida en Bloque",
        quantity: 24,
        unit: "kg",
        cost: 288.0,
        services: 5,
      },
      {
        material: "Gel Cucarachas",
        quantity: 18,
        unit: "tubos",
        cost: 135.0,
        services: 6,
      },
      {
        material: "Trampas Adhesivas",
        quantity: 40,
        unit: "ud",
        cost: 60.0,
        services: 4,
      },
    ],
    month: [
      {
        material: "Insecticida Premium",
        quantity: 60,
        unit: "L",
        cost: 1200.0,
        services: 32,
      },
      {
        material: "Raticida en Bloque",
        quantity: 96,
        unit: "kg",
        cost: 1152.0,
        services: 20,
      },
      {
        material: "Gel Cucarachas",
        quantity: 72,
        unit: "tubos",
        cost: 540.0,
        services: 24,
      },
      {
        material: "Trampas Adhesivas",
        quantity: 160,
        unit: "ud",
        cost: 240.0,
        services: 16,
      },
    ],
    quarter: [
      {
        material: "Insecticida Premium",
        quantity: 180,
        unit: "L",
        cost: 3600.0,
        services: 96,
      },
      {
        material: "Raticida en Bloque",
        quantity: 288,
        unit: "kg",
        cost: 3456.0,
        services: 60,
      },
      {
        material: "Gel Cucarachas",
        quantity: 216,
        unit: "tubos",
        cost: 1620.0,
        services: 72,
      },
      {
        material: "Trampas Adhesivas",
        quantity: 480,
        unit: "ud",
        cost: 720.0,
        services: 48,
      },
    ],
    year: [
      {
        material: "Insecticida Premium",
        quantity: 720,
        unit: "L",
        cost: 14400.0,
        services: 384,
      },
      {
        material: "Raticida en Bloque",
        quantity: 1152,
        unit: "kg",
        cost: 13824.0,
        services: 240,
      },
      {
        material: "Gel Cucarachas",
        quantity: 864,
        unit: "tubos",
        cost: 6480.0,
        services: 288,
      },
      {
        material: "Trampas Adhesivas",
        quantity: 1920,
        unit: "ud",
        cost: 2880.0,
        services: 192,
      },
    ],
  };

  // Datos mock - KPIs
  const kpisData = {
    week: {
      servicesCompleted: 30,
      avgResponseTime: 2.5,
      customerSatisfaction: 94.5,
      technicianEfficiency: 92.2,
      materialUtilization: 88.5,
      revenuePerService: 237.0,
      costPerService: 142.2,
      profitMargin: 40.0,
    },
    month: {
      servicesCompleted: 125,
      avgResponseTime: 2.8,
      customerSatisfaction: 93.8,
      technicianEfficiency: 91.8,
      materialUtilization: 87.2,
      revenuePerService: 235.6,
      costPerService: 145.5,
      profitMargin: 38.3,
    },
    quarter: {
      servicesCompleted: 377,
      avgResponseTime: 2.6,
      customerSatisfaction: 94.2,
      technicianEfficiency: 92.0,
      materialUtilization: 88.0,
      revenuePerService: 236.2,
      costPerService: 143.8,
      profitMargin: 39.1,
    },
    year: {
      servicesCompleted: 1508,
      avgResponseTime: 2.7,
      customerSatisfaction: 94.0,
      technicianEfficiency: 91.7,
      materialUtilization: 87.8,
      revenuePerService: 236.0,
      costPerService: 144.5,
      profitMargin: 38.8,
    },
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const getPeriodLabel = (period: ReportPeriod): string => {
    const labels = {
      week: "Esta Semana",
      month: "Este Mes",
      quarter: "Este Trimestre",
      year: "Este Año",
    };
    return labels[period];
  };

  const currentTechnicians = technicianServices[period];
  const currentCosts = costsData[period];
  const currentMaterials = materialsConsumption[period];
  const currentKPIs = kpisData[period];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>📊 Informes y Estadísticas</h1>
          <p className={styles.subtitle}>
            Análisis detallado del desempeño operativo
          </p>
        </div>
      </div>

      {/* Controles */}
      <div className={styles.controls}>
        <div className={styles.periodSelector}>
          <button
            className={`${styles.periodButton} ${period === "week" ? styles.active : ""}`}
            onClick={() => setPeriod("week")}
          >
            Semana
          </button>
          <button
            className={`${styles.periodButton} ${period === "month" ? styles.active : ""}`}
            onClick={() => setPeriod("month")}
          >
            Mes
          </button>
          <button
            className={`${styles.periodButton} ${period === "quarter" ? styles.active : ""}`}
            onClick={() => setPeriod("quarter")}
          >
            Trimestre
          </button>
          <button
            className={`${styles.periodButton} ${period === "year" ? styles.active : ""}`}
            onClick={() => setPeriod("year")}
          >
            Año
          </button>
        </div>

        <div className={styles.reportTypeSelector}>
          <button
            className={`${styles.typeButton} ${reportType === "technicians" ? styles.active : ""}`}
            onClick={() => setReportType("technicians")}
          >
            <Icon name="users" size={18} />
            Por Técnico
          </button>
          <button
            className={`${styles.typeButton} ${reportType === "costs" ? styles.active : ""}`}
            onClick={() => setReportType("costs")}
          >
            <Icon name="creditCard" size={18} />
            Costes
          </button>
          <button
            className={`${styles.typeButton} ${reportType === "materials" ? styles.active : ""}`}
            onClick={() => setReportType("materials")}
          >
            <Icon name="document" size={18} />
            Materiales
          </button>
          <button
            className={`${styles.typeButton} ${reportType === "kpis" ? styles.active : ""}`}
            onClick={() => setReportType("kpis")}
          >
            <Icon name="dashboard" size={18} />
            KPIs
          </button>
        </div>
      </div>

      {/* Informe de Servicios por Técnico */}
      {reportType === "technicians" && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>👷 Servicios por Técnico - {getPeriodLabel(period)}</h2>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Técnico</th>
                  <th>Servicios</th>
                  <th>Horas Trabajadas</th>
                  <th>Eficiencia</th>
                  <th>Ingresos Generados</th>
                  <th>Ingreso/Servicio</th>
                </tr>
              </thead>
              <tbody>
                {currentTechnicians.map((tech, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{tech.name}</strong>
                    </td>
                    <td>{tech.services}</td>
                    <td>{tech.hours}h</td>
                    <td>
                      <span
                        className={styles.badge}
                        style={{
                          backgroundColor:
                            tech.efficiency >= 93
                              ? "#dcfce7"
                              : tech.efficiency >= 90
                                ? "#fef3c7"
                                : "#fee2e2",
                          color:
                            tech.efficiency >= 93
                              ? "#166534"
                              : tech.efficiency >= 90
                                ? "#92400e"
                                : "#991b1b",
                        }}
                      >
                        {tech.efficiency}%
                      </span>
                    </td>
                    <td>{formatCurrency(tech.revenue)}</td>
                    <td>{formatCurrency(tech.revenue / tech.services)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>TOTAL</strong>
                  </td>
                  <td>
                    <strong>
                      {currentTechnicians.reduce(
                        (acc, t) => acc + t.services,
                        0,
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {currentTechnicians.reduce((acc, t) => acc + t.hours, 0)}h
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {(
                        currentTechnicians.reduce(
                          (acc, t) => acc + t.efficiency,
                          0,
                        ) / currentTechnicians.length
                      ).toFixed(1)}
                      %
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        currentTechnicians.reduce(
                          (acc, t) => acc + t.revenue,
                          0,
                        ),
                      )}
                    </strong>
                  </td>
                  <td>—</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Informe de Costes */}
      {reportType === "costs" && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>💰 Análisis de Costes - {getPeriodLabel(period)}</h2>
          </div>

          {/* Costes por Cliente */}
          <h3 className={styles.subsectionTitle}>Por Cliente</h3>
          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Servicios</th>
                  <th>Materiales</th>
                  <th>Mano de Obra</th>
                  <th>Total</th>
                  <th>Coste/Servicio</th>
                </tr>
              </thead>
              <tbody>
                {/* @ts-ignore - Cost data structure varies by period */}
                {(period === "week"
                  ? // @ts-ignore
                    currentCosts.byClient
                  : // @ts-ignore
                    currentCosts.byClient || currentCosts
                ).map((client: any, index: number) => (
                  <tr key={index}>
                    <td>
                      <strong>{client.name}</strong>
                    </td>
                    <td>{client.services}</td>
                    <td>{formatCurrency(client.materials)}</td>
                    <td>{formatCurrency(client.labor)}</td>
                    <td>
                      <strong>{formatCurrency(client.total)}</strong>
                    </td>
                    <td>{formatCurrency(client.total / client.services)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Costes por Plaga */}
          {(period === "week" || period === "quarter" || period === "year") && (
            <>
              <h3 className={styles.subsectionTitle}>Por Tipo de Plaga</h3>
              <div className={styles.tableContainer}>
                <table className={styles.reportTable}>
                  <thead>
                    <tr>
                      <th>Plaga</th>
                      <th>Incidencias</th>
                      <th>Materiales</th>
                      <th>Mano de Obra</th>
                      <th>Total</th>
                      <th>Coste/Incidencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* @ts-expect-error: Cost data structure varies - needs proper type guards */}
                    {currentCosts.byPest.map((pest: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <strong>{pest.pest}</strong>
                        </td>
                        <td>{pest.occurrences}</td>
                        <td>{formatCurrency(pest.materials)}</td>
                        <td>{formatCurrency(pest.labor)}</td>
                        <td>
                          <strong>{formatCurrency(pest.total)}</strong>
                        </td>
                        <td>{formatCurrency(pest.total / pest.occurrences)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Informe de Consumo de Materiales */}
      {reportType === "materials" && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>📦 Consumo de Materiales - {getPeriodLabel(period)}</h2>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Cantidad Consumida</th>
                  <th>Coste Total</th>
                  <th>Servicios Realizados</th>
                  <th>Consumo/Servicio</th>
                  <th>Coste/Servicio</th>
                </tr>
              </thead>
              <tbody>
                {currentMaterials.map((material, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{material.material}</strong>
                    </td>
                    <td>
                      {material.quantity} {material.unit}
                    </td>
                    <td>{formatCurrency(material.cost)}</td>
                    <td>{material.services}</td>
                    <td>
                      {(material.quantity / material.services).toFixed(2)}{" "}
                      {material.unit}
                    </td>
                    <td>{formatCurrency(material.cost / material.services)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>TOTAL</strong>
                  </td>
                  <td>—</td>
                  <td>
                    <strong>
                      {formatCurrency(
                        currentMaterials.reduce((acc, m) => acc + m.cost, 0),
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {currentMaterials.reduce((acc, m) => acc + m.services, 0)}
                    </strong>
                  </td>
                  <td>—</td>
                  <td>
                    <strong>
                      {formatCurrency(
                        currentMaterials.reduce((acc, m) => acc + m.cost, 0) /
                          currentMaterials.reduce(
                            (acc, m) => acc + m.services,
                            0,
                          ),
                      )}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* KPIs Operativos */}
      {reportType === "kpis" && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>📈 KPIs Operativos - {getPeriodLabel(period)}</h2>
          </div>

          <div className={styles.kpisGrid}>
            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#dbeafe", color: "#1e40af" }}
              >
                <Icon name="clipboard" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Servicios Completados</span>
                <span className={styles.kpiValue}>
                  {currentKPIs.servicesCompleted}
                </span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#dcfce7", color: "#166534" }}
              >
                <Icon name="clock" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>
                  Tiempo de Respuesta Promedio
                </span>
                <span className={styles.kpiValue}>
                  {currentKPIs.avgResponseTime}h
                </span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
              >
                <Icon name="star" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>
                  Satisfacción del Cliente
                </span>
                <span className={styles.kpiValue}>
                  {currentKPIs.customerSatisfaction}%
                </span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#e0e7ff", color: "#4338ca" }}
              >
                <Icon name="users" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Eficiencia de Técnicos</span>
                <span className={styles.kpiValue}>
                  {currentKPIs.technicianEfficiency}%
                </span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#fce7f3", color: "#9f1239" }}
              >
                <Icon name="document" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>
                  Utilización de Materiales
                </span>
                <span className={styles.kpiValue}>
                  {currentKPIs.materialUtilization}%
                </span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#d1fae5", color: "#065f46" }}
              >
                <Icon name="creditCard" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Ingreso por Servicio</span>
                <span className={styles.kpiValue}>
                  {formatCurrency(currentKPIs.revenuePerService)}
                </span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#fed7aa", color: "#9a3412" }}
              >
                <Icon name="creditCard" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Coste por Servicio</span>
                <span className={styles.kpiValue}>
                  {formatCurrency(currentKPIs.costPerService)}
                </span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <div
                className={styles.kpiIcon}
                style={{ backgroundColor: "#ccfbf1", color: "#134e4a" }}
              >
                <Icon name="dashboard" size={28} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Margen de Beneficio</span>
                <span className={styles.kpiValue}>
                  {currentKPIs.profitMargin}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
